import { makeAutoObservable, action, runInAction } from "mobx";
import publicApiClient from "utils/publicApiClient";

// Helper function to encode URL in base64
const b64EncodeUnicode = (str: string) => {
  return btoa(unescape(encodeURIComponent(str)));
};

// Helper function to decode base64 URL
const b64DecodeUnicode = (str: string) => {
  return decodeURIComponent(escape(atob(str)));
};

interface IExplorer {
  name: string;
  url: string;
  mostUsed?: boolean;
  novncConfig?: {
    containerId: string;
    vncUrl: string;
    runningUntil: string;
  };
}

interface IChain {
  name: string;
  shortName: string;
  logo: string;
  explorers: IExplorer[];
}

interface ISynkChainScanPageStore {
  isSidebarOpen: boolean;
  isDrawerOpen: boolean;
  isFullscreen: boolean;
  isDarkMode: boolean;
  selectedChain: IChain | null;
  selectedExplorer: IExplorer | null;
  chains: IChain[];
  currentUrl: string;
  isLoading: boolean;
  error: string | null;
  isVNCReady: boolean;
  loadingStep: number;
  initialize: () => Promise<void>;
  currentConnectionAttempt?: {
    controller: AbortController;
    timeoutId?: NodeJS.Timeout;
  };
}

class SynkChainScanPageStore implements ISynkChainScanPageStore {
  isSidebarOpen = false;
  isDrawerOpen = false;
  isFullscreen = false;
  isDarkMode = true;
  selectedChain: IChain | null = null;
  selectedExplorer: IExplorer | null = null;
  currentUrl = "";
  isLoading = false;
  error: string | null = null;
  isVNCReady = false;
  loadingStep = 0;
  currentConnectionAttempt?: {
    controller: AbortController;
    timeoutId?: NodeJS.Timeout;
  };
  private wsConnection: WebSocket | null = null;
  private connectionAttempts = 0;
  private readonly MAX_CONNECTION_ATTEMPTS = 30;
  private readonly CONNECTION_INTERVAL = 2000;
  private connectionTimeout: NodeJS.Timeout | null = null;
  chains: IChain[] = [
    {
      name: "Ethereum",
      shortName: "ETH",
      logo: "/images/crypto/ethereum.svg",
      explorers: [
        {
          name: "Etherscan",
          url: "https://etherscan.io",
          mostUsed: true,
        },
        {
          name: "Ethplorer",
          url: "https://ethplorer.io",
        },
        {
          name: "Blockchair",
          url: "https://blockchair.com/ethereum",
        },
        {
          name: "EthVM",
          url: "https://ethvm.com",
        },
      ],
    },
    {
      name: "Bitcoin",
      shortName: "BTC",
      logo: "/images/crypto/bitcoin.svg",
      explorers: [
        {
          name: "Blockchain.com",
          url: "https://www.blockchain.com",
          mostUsed: true,
        },
        {
          name: "Blockstream",
          url: "https://blockstream.info",
          mostUsed: true,
        },
        {
          name: "Blockchair",
          url: "https://blockchair.com/bitcoin",
        },
        {
          name: "BTC.com",
          url: "https://btc.com",
        },
        {
          name: "Mempool Space",
          url: "https://mempool.space",
        },
      ],
    },
    {
      name: "BNB Chain",
      shortName: "BNB",
      logo: "/images/crypto/bnb.svg",
      explorers: [
        {
          name: "BscScan",
          url: "https://bscscan.com",
        },
      ],
    },
    {
      name: "Solana",
      shortName: "SOL",
      logo: "/images/crypto/solana.svg",
      explorers: [
        {
          name: "Solscan",
          url: "https://solscan.io",
          mostUsed: true,
        },
        {
          name: "Solana Explorer",
          url: "https://explorer.solana.com",
        },
        {
          name: "Solana FM",
          url: "https://solana.fm",
        },
      ],
    },
    {
      name: "Polygon",
      shortName: "MATIC",
      logo: "/images/crypto/polygon.svg",
      explorers: [
        {
          name: "Polygonscan",
          url: "https://polygonscan.com",
        },
      ],
    },
    {
      name: "Avalanche",
      shortName: "AVAX",
      logo: "/images/crypto/avalanche.svg",
      explorers: [
        {
          name: "Snowtrace",
          url: "https://snowtrace.io",
        },
      ],
    },
    {
      name: "Arbitrum",
      shortName: "ARB",
      logo: "/images/crypto/arbitrum.svg",
      explorers: [
        {
          name: "Arbiscan",
          url: "https://arbiscan.io",
        },
      ],
    },
    {
      name: "Optimism",
      shortName: "OP",
      logo: "/images/crypto/optimism.svg",
      explorers: [
        {
          name: "Optimistic Etherscan",
          url: "https://optimistic.etherscan.io",
        },
      ],
    },
  ];

  constructor() {
    makeAutoObservable(this, {
      setLoadingStep: action,
      setVNCReady: action,
      setLoading: action,
      setError: action,
      setConnected: action,
      toggleTheme: action,
      toggleFullscreen: action,
      toggleSidebar: action,
      toggleDrawer: action,
      selectChain: action,
      selectExplorer: action,
      navigateTo: action,
      refresh: action,
      initialize: action,
      startVNCContainer: action,
      checkVNCConnection: action,
      handleConnectionError: action,
    });
    const savedTheme = localStorage.getItem("synkExplorerTheme");
    if (savedTheme) {
      this.isDarkMode = savedTheme === "dark";
    }
  }

  setLoadingStep = (step: number) => {
    this.loadingStep = step;
  };

  setVNCReady = (ready: boolean) => {
    this.isVNCReady = ready;
  };

  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setConnected = (connected: boolean) => {
    this.isVNCReady = connected;
  };

  toggleTheme = () => {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem(
      "synkExplorerTheme",
      this.isDarkMode ? "dark" : "light"
    );
  };

  private updateUrlWithVNCParams(containerId: string, vncUrl: string) {
    const url = new URL(window.location.href);
    url.searchParams.set("containerId", containerId);
    url.searchParams.set("vncUrl", b64EncodeUnicode(vncUrl));
    window.history.replaceState({}, "", url.toString());
  }

  private getVNCParamsFromUrl(): {
    containerId: string;
    vncUrl: string;
  } | null {
    const url = new URL(window.location.href);
    const containerId = url.searchParams.get("containerId");
    const encodedVncUrl = url.searchParams.get("vncUrl");

    if (containerId && encodedVncUrl) {
      try {
        const vncUrl = b64DecodeUnicode(encodedVncUrl);
        return { containerId, vncUrl };
      } catch (error) {
        console.error("Error decoding VNC URL:", error);
        return null;
      }
    }
    return null;
  }

  initialize = async () => {
    const defaultChain = this.chains[0];
    const defaultExplorer = defaultChain.explorers[0];
    this.selectedChain = defaultChain;
    this.selectedExplorer = defaultExplorer;
    this.setLoading(true);
    this.setLoadingStep(0);
    this.setError(null);
    this.setVNCReady(false);

    // Check if we have VNC parameters in the URL
    const vncParams = this.getVNCParamsFromUrl();

    if (vncParams) {
      // If we have VNC parameters, use them directly
      if (this.selectedExplorer) {
        this.selectedExplorer.novncConfig = {
          containerId: vncParams.containerId,
          vncUrl: vncParams.vncUrl,
          runningUntil: new Date(Date.now() + 3600000).toISOString(), // 1 hour from now
        };
      }
      this.setLoadingStep(2);
      await this.checkVNCConnection(vncParams.vncUrl);
    } else {
      // If no VNC parameters, start a new container
      await this.startVNCContainer(defaultExplorer.url);
    }
  };

  toggleFullscreen = () => {
    this.isFullscreen = !this.isFullscreen;
  };

  toggleSidebar = () => {
    this.isSidebarOpen = !this.isSidebarOpen;
  };

  toggleDrawer = () => {
    this.isDrawerOpen = !this.isDrawerOpen;
  };

  selectChain = async (chain: IChain) => {
    console.log("selectChain", chain);
  };

  selectExplorer = async (explorer: IExplorer) => {
    if (!this.selectedExplorer?.novncConfig?.containerId) return;

    try {
      await publicApiClient.post(
        `/v1/demo/${this.selectedExplorer.novncConfig.containerId}/do`,
        {
          url: explorer.url,
        }
      );
      this.selectedExplorer = {
        ...explorer,
        novncConfig: this.selectedExplorer.novncConfig,
      };
    } catch (err) {
      console.error("Failed to navigate to explorer:", err);
    }
  };

  startVNCContainer = async (url: string) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      this.handleConnectionError("Patience is a virtue. So is refreshing.");
    }, 5000);

    this.currentConnectionAttempt = { controller, timeoutId };
    this.setLoadingStep(1);

    try {
      const base64Url = b64EncodeUnicode(url);
      const response = await publicApiClient.post(
        `/v1/demo/start?open_url=${base64Url}`,
        { signal: controller.signal }
      );

      if (response.data?.success) {
        const { screen_url, appInstanceId, runningUntil } =
          response.data.details;

        if (this.selectedExplorer) {
          this.selectedExplorer.novncConfig = {
            containerId: appInstanceId,
            vncUrl: screen_url,
            runningUntil,
          };
        }

        // Update URL with VNC parameters
        this.updateUrlWithVNCParams(appInstanceId, screen_url);

        this.navigateTo(screen_url);
        this.setLoadingStep(2);
        await this.checkVNCConnection(screen_url);
      } else {
        throw new Error(
          "Our explorer got lost in the blockchain jungle. Let's try again!"
        );
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        console.log("Connection attempt aborted");
        return;
      }
      this.handleConnectionError(
        err instanceof Error
          ? err.message
          : "The blockchain gremlins are at it again!"
      );
    } finally {
      if (this.currentConnectionAttempt) {
        clearTimeout(this.currentConnectionAttempt.timeoutId);
        this.currentConnectionAttempt = undefined;
      }
    }
  };

  private cleanupWebSocket = () => {
    if (this.wsConnection) {
      this.wsConnection.close();
      this.wsConnection = null;
    }
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  };

  checkVNCConnection = async (vncUrl: string) => {
    this.cleanupWebSocket();
    this.connectionAttempts = 0;

    const attemptConnection = () => {
      if (this.connectionAttempts >= this.MAX_CONNECTION_ATTEMPTS) {
        this.handleConnectionError(
          "Our explorer seems to be taking a nap. Let's wake it up with a refresh!"
        );
        return;
      }

      try {
        this.wsConnection = new WebSocket(vncUrl);

        this.wsConnection.onopen = () => {
          console.log("WSS connection established successfully");
          runInAction(() => {
            this.setLoadingStep(6);
          });

          const vncParams = this.getVNCParamsFromUrl();
          const delay = vncParams ? 1500 : 3500;

          setTimeout(() => {
            runInAction(() => {
              this.setVNCReady(true);
              this.setLoading(false);
            });
          }, delay);
        };

        this.wsConnection.onerror = () => {
          console.log("WSS connection error");
          this.connectionAttempts++;
          this.cleanupWebSocket();
          this.connectionTimeout = setTimeout(
            attemptConnection,
            this.CONNECTION_INTERVAL
          );
        };

        this.wsConnection.onclose = () => {
          console.log("WSS connection closed");
          this.connectionAttempts++;
          this.cleanupWebSocket();
          this.connectionTimeout = setTimeout(
            attemptConnection,
            this.CONNECTION_INTERVAL
          );
        };
      } catch (err) {
        console.log("WSS connection attempt failed:", err);
        this.connectionAttempts++;
        this.cleanupWebSocket();
        this.connectionTimeout = setTimeout(
          attemptConnection,
          this.CONNECTION_INTERVAL
        );
      }
    };

    attemptConnection();
  };

  handleConnectionError = (errorMessage: string) => {
    this.cleanupWebSocket();
    this.setError(errorMessage);
    this.setLoading(false);
    this.setVNCReady(false);
    this.setLoadingStep(0);
  };

  navigateTo = (url: string) => {
    this.currentUrl = url;
  };

  refresh = () => {
    if (this.selectedExplorer) {
      this.startVNCContainer(this.selectedExplorer.url);
    }
  };
}

export const synkChainScanPageStore = new SynkChainScanPageStore();
export default synkChainScanPageStore;
